import { useEffect } from 'react';

import { useLazyQuery } from '@apollo/client';

import { IFlipperEnum } from '@r-client/data/graphql-types';
import { useAuth } from '@r-client/shared/data/auth';
import { useGqlClient } from '@r-client/shared/data/client';
import { useGetFeatureToggleQuery } from '@r-client/shared/data/flags/remote';
import {
  GetCryptoWalletsDocument,
  IGetCryptoWalletsQuery,
} from '@r-client/wallet/data/republic-sync';
import { useHasWalletQuery } from '@r-client/wallet/data/warp-drive';

export const useHasWallet = () => {
  const { viewer } = useAuth();
  const { data: flagData } = useGetFeatureToggleQuery({
    variables: { feature: IFlipperEnum.GlobalWalletService },
  });
  const isWarpDriveEnabled = !!flagData?.flipper?.enabled;

  // Warp Drive path
  const hasWalletQuery = useHasWalletQuery({ lazy: true });

  // Legacy path
  const legacyClient = useGqlClient();
  const [
    getCryptoWallets,
    {
      data: cryptoWalletData,
      loading: cryptoWalletIsLoading,
      error: cryptoWalletQueryErrors,
    },
  ] = useLazyQuery<IGetCryptoWalletsQuery>(GetCryptoWalletsDocument, {
    client: legacyClient,
  });

  useEffect(() => {
    if (!viewer?.isAuthenticated) return;

    if (isWarpDriveEnabled) {
      hasWalletQuery.subscribe();
    } else {
      getCryptoWallets({
        variables: { investorProfileId: viewer?.info?.investorProfile?.id },
      });
    }
  }, [
    viewer?.isAuthenticated,
    viewer?.info?.investorProfile?.id,
    isWarpDriveEnabled,
    hasWalletQuery,
    getCryptoWallets,
  ]);

  if (isWarpDriveEnabled) {
    const hasWallet =
      !hasWalletQuery.isRejected &&
      !hasWalletQuery.isLoading &&
      !!hasWalletQuery.data?.hasWallet;

    return {
      hasWallet,
      hasRepublicWallet: hasWallet,
      hasExternalWallet: false,
      cryptoWalletIsLoading: hasWalletQuery.isLoading,
    };
  }

  const hasWallet =
    !cryptoWalletQueryErrors &&
    !cryptoWalletIsLoading &&
    !!cryptoWalletData?.cryptoWallets?.nodes.length;

  const hasRepublicWallet = !!cryptoWalletData?.cryptoWallets?.nodes.some(
    (wallet) => wallet.republicWallet
  );

  const hasExternalWallet = !!cryptoWalletData?.cryptoWallets?.nodes.some(
    (wallet) => !wallet.republicWallet
  );

  return {
    hasWallet,
    hasRepublicWallet,
    hasExternalWallet,
    cryptoWalletIsLoading,
  };
};
